import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Search, ArrowRight, BookOpen, Calendar, Tag as TagIcon } from "lucide-react";
import BottomNav from "@/components/site/BottomNav";
import { LOGO_URL } from "@/lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "long", day: "numeric",
    });
  } catch { return iso?.slice(0, 10) || ""; }
};

const decodeEntities = (s = "") =>
  s.replace(/&#8217;/g, "’").replace(/&#8216;/g, "‘")
    .replace(/&#038;/g, "&").replace(/&amp;/g, "&")
    .replace(/&#8211;/g, "–").replace(/&#8212;/g, "—")
    .replace(/&quot;/g, "\"").replace(/&#39;/g, "'");

export default function Devotionals() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    axios.get(`${API}/devotionals`).then((r) => setItems(r.data || [])).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const term = q.toLowerCase();
    return items.filter((d) => {
      const hay = [
        d.title, d.excerpt,
        ...(d.category_names || []), ...(d.tag_names || []),
      ].join(" ").toLowerCase();
      return hay.includes(term);
    });
  }, [items, q]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div data-testid="devotionals-page">
      {/* HERO */}
      <section className="page-hero" style={{ backgroundColor: "var(--navy-800)" }}>
        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "radial-gradient(circle at 80% 20%, rgba(217,184,100,0.5) 0%, transparent 40%)" }} />
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
          <div className="grid lg:grid-cols-3 gap-10 items-end">
            <div className="lg:col-span-2">
              <div className="text-xs uppercase tracking-[0.32em] text-gold-light mb-4">Daily Devotionals</div>
              <h1 className="font-display text-4xl lg:text-6xl text-cream leading-tight">
                A quiet moment with Scripture, every day.
              </h1>
              <p className="mt-5 text-cream/80 text-lg max-w-2xl leading-relaxed">
                Short, Christ-centered devotionals to anchor your morning, soften your heart, and remind you who you belong to.
              </p>
              <p className="mt-3 text-cream/60 text-sm" data-testid="devotionals-count">
                {items.length} devotional{items.length !== 1 ? "s" : ""} published
              </p>
            </div>
            <div className="relative">
              <label className="block text-cream/70 text-xs uppercase tracking-[0.25em] mb-2">Search devotionals</label>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-navy/50" size={18} />
                <input
                  data-testid="devotionals-search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  type="text"
                  placeholder="Search by title or topic..."
                  className="w-full pl-12 pr-4 py-3.5 rounded-full bg-cream-50 text-navy-deep border border-cream-200 focus-gold focus:outline-none"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED DEVOTIONAL */}
      {featured && (
        <section className="py-20 bg-cream">
          <div className="max-w-6xl mx-auto px-5 lg:px-8">
            <div className="ornament mb-8">
              <span className="text-xs uppercase tracking-[0.3em] text-gold font-medium">Today's Featured Devotional</span>
            </div>
            <article data-testid="devotional-card-featured" className="grid lg:grid-cols-12 gap-8 lg:gap-12">
              <div className="lg:col-span-5">
                <div className="relative aspect-[4/5] rounded-2xl overflow-hidden">
                  {featured.featured_image_url ? (
                    <img
                      src={featured.featured_image_url}
                      alt={decodeEntities(featured.title)}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center p-8 text-center text-cream"
                      style={{ background: "linear-gradient(135deg, var(--navy-800), var(--navy-600) 50%, var(--gold-500))" }}
                    >
                      <img src={LOGO_URL} alt="" className="w-32 h-32 object-contain mb-4 drop-shadow-2xl" />
                      <BookOpen size={28} className="text-gold-light" strokeWidth={1.4} />
                    </div>
                  )}
                </div>
              </div>
              <div className="lg:col-span-7 flex flex-col justify-center">
                <div className="flex items-center gap-3 text-xs text-navy/60 mb-3 flex-wrap">
                  <Calendar size={14} className="text-gold" />
                  <span>{fmtDate(featured.published_at)}</span>
                  {featured.category_names?.slice(0, 2).map((c) => (
                    <span key={c} className="text-gold uppercase tracking-wider font-semibold">· {decodeEntities(c)}</span>
                  ))}
                </div>
                <h2 className="font-display text-3xl lg:text-5xl text-navy-deep leading-tight">{decodeEntities(featured.title)}</h2>
                <p className="mt-5 text-navy/75 text-lg leading-relaxed">{decodeEntities(featured.excerpt)}</p>
                <Link
                  to={`/devotionals/${featured.slug}`}
                  data-testid="devotionals-featured-cta"
                  className="mt-7 inline-flex items-center gap-2 btn-gold px-7 py-3 rounded-full self-start"
                >
                  Read the Devotional <ArrowRight size={16} />
                </Link>
              </div>
            </article>
          </div>
        </section>
      )}

      {/* RECENT — card grid */}
      <section className="py-20 lg:py-24" style={{ backgroundColor: "var(--cream-100)" }}>
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-gold font-medium mb-2">Library</div>
              <h2 className="font-display text-3xl lg:text-4xl text-navy-deep">Recent Devotionals</h2>
            </div>
            <p className="text-sm text-navy/60">
              {filtered.length} of {items.length} shown
            </p>
          </div>

          {filtered.length === 0 && (
            <p className="text-center text-navy/60 py-12" data-testid="devotionals-empty">
              No devotionals match your search.
            </p>
          )}

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((d) => (
              <Link
                to={`/devotionals/${d.slug}`}
                key={d.slug}
                data-testid={`devotional-card-${d.slug}`}
                className="lift bg-white rounded-2xl overflow-hidden border border-cream-200 hover:border-gold flex flex-col"
              >
                <div className="relative h-44 overflow-hidden">
                  {d.featured_image_url ? (
                    <img src={d.featured_image_url} alt={decodeEntities(d.title)} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, var(--navy-800), var(--gold-500))" }}>
                      <img src={LOGO_URL} alt="" className="w-20 h-20 object-contain drop-shadow-xl opacity-90" />
                    </div>
                  )}
                  <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-cream-50/95 text-navy-deep text-[0.7rem] font-semibold uppercase tracking-wider flex items-center gap-1.5">
                    <Calendar size={11} className="text-gold" /> {fmtDate(d.published_at)}
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="font-display text-xl text-navy-deep leading-snug">{decodeEntities(d.title)}</h3>
                  {d.category_names?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {d.category_names.slice(0, 2).map((c) => (
                        <span key={c} className="inline-flex items-center gap-1 text-[0.65rem] uppercase tracking-wider text-gold font-semibold">
                          <TagIcon size={10} /> {decodeEntities(c)}
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="mt-3 text-navy/70 text-sm leading-relaxed flex-1">{decodeEntities(d.excerpt)}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-navy-deep font-semibold text-sm group-hover:text-gold">
                    Read More <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <BottomNav exclude={["/devotionals"]} />
    </div>
  );
}
